/**
 * Barra de Ferramentas Unificada do Mapa Interativo (LegisCity).
 *
 * Consolida em uma única estação ergonômica flutuante:
 * 1. Controles de Zoom (Aproximar / Afastar)
 * 2. Ferramentas de Localização (Visão geral de Artur Nogueira + Minha Localização GPS)
 * 3. Alternador de Camada Térmica (Mapa de Calor)
 * 4. Alternador de Concentração por Bairro (Mapa Coroplético em 5 Faixas)
 * 5. Legenda Cartográfica Dinâmica (Temas, Densidade Térmica ou Escala Coroplética)
 *
 * Posicionamento responsivo com ancoragem inteligente ao lado do painel lateral.
 *
 * @module components/MapToolbar
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

import {
  THEMATIC_CATEGORIES,
  THEMATIC_CATEGORIES_ORDER,
  GEOMETRY_TYPE_METADATA,
} from "../constants/categories";
import {
  CHOROPLETH_SELECTED_STROKE,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  computeChoroplethBreaks,
  getChoroplethPalette,
} from "../constants/mapConfig";
import { useFilters } from "../hooks/useFilters";
import GeometryIcon from "./GeometryIcon";

/**
 * @param {object} props
 * @param {boolean} [props.isHeatmapActive=false]
 * @param {() => void} [props.onToggleHeatmap]
 * @param {boolean} [props.isChoroplethActive=false]
 * @param {() => void} [props.onToggleChoropleth]
 * @param {(layerMode: "clusters" | "heatmap" | "choropleth") => void} [props.onSelectLayer]
 * @param {any} [props.choroplethMeta=null]
 * @param {boolean} [props.isDarkMode=false]
 * @param {boolean} [props.statsPanelCollapsed=true]
 * @param {boolean} [props.isMobile=false]
 */
export default function MapToolbar({
  isHeatmapActive = false,
  onToggleHeatmap,
  isChoroplethActive = false,
  onToggleChoropleth,
  onSelectLayer,
  choroplethMeta = null,
  isDarkMode = false,
  statsPanelCollapsed = true,
  isMobile = false,
}) {
  const map = useMap();
  const toolbarRef = useRef(null);
  const popoverRef = useRef(null);
  const layersPopoverRef = useRef(null);
  const toastTimeoutRef = useRef(null);

  const [isLayersOpen, setIsLayersOpen] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const activeLayerMode = isChoroplethActive
    ? "choropleth"
    : isHeatmapActive
    ? "heatmap"
    : "clusters";

  const { filters, setThematicCategory } = useFilters();
  const activeCategory = filters.thematic_category;

  const choroplethPalette = getChoroplethPalette(isDarkMode);
  const choroplethBreaks =
    choroplethMeta?.breaks?.length === 5
      ? choroplethMeta.breaks
      : computeChoroplethBreaks(choroplethMeta?.maxCount || 0);
  const selectedStrokeColor = isDarkMode
    ? CHOROPLETH_SELECTED_STROKE.dark
    : CHOROPLETH_SELECTED_STROKE.light;

  const handleSelectLayerMode = useCallback(
    (mode) => {
      if (typeof onSelectLayer === "function") {
        onSelectLayer(mode);
      } else {
        if (mode === "heatmap" && !isHeatmapActive) {
          onToggleHeatmap?.();
        } else if (mode === "choropleth" && !isChoroplethActive) {
          onToggleChoropleth?.();
        } else if (mode === "clusters") {
          if (isHeatmapActive) onToggleHeatmap?.();
          if (isChoroplethActive) onToggleChoropleth?.();
        }
      }
      setIsLayersOpen(false);
    },
    [onSelectLayer, isHeatmapActive, isChoroplethActive, onToggleHeatmap, onToggleChoropleth]
  );

  // Mostra mensagem temporária tipo toast para feedback de localização
  const showToast = useCallback((msg) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(msg);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 3200);
  }, []);

  // Previne que cliques e scrolls nos controles propaguem para o mapa Leaflet
  useEffect(() => {
    const el = toolbarRef.current;
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, []);

  useEffect(() => {
    const el = popoverRef.current;
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, [isLegendOpen]);

  useEffect(() => {
    const el = layersPopoverRef.current;
    if (el) {
      L.DomEvent.disableClickPropagation(el);
      L.DomEvent.disableScrollPropagation(el);
    }
  }, [isLayersOpen]);

  // Fecha os popovers de Camadas ou Legenda com a tecla Escape ou clique fora
  useEffect(() => {
    if (!isLegendOpen && !isLayersOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsLegendOpen(false);
        setIsLayersOpen(false);
      }
    };

    const handleClickOutside = (e) => {
      const clickedInsideToolbar = toolbarRef.current?.contains(e.target);
      const clickedInsideLegend = popoverRef.current?.contains(e.target);
      const clickedInsideLayers = layersPopoverRef.current?.contains(e.target);

      if (!clickedInsideToolbar && !clickedInsideLegend && !clickedInsideLayers) {
        setIsLegendOpen(false);
        setIsLayersOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLegendOpen, isLayersOpen]);

  // Limpa timeout ao desmontar
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // Ações de Zoom
  const handleZoomIn = (e) => {
    e.stopPropagation();
    map.zoomIn();
  };

  const handleZoomOut = (e) => {
    e.stopPropagation();
    map.zoomOut();
  };

  // Visão Geral de Artur Nogueira
  const handleResetView = (e) => {
    e.stopPropagation();
    map.flyTo(DEFAULT_CENTER, DEFAULT_ZOOM, { duration: 0.8 });
  };

  // Minha Localização GPS
  const handleMyLocation = (e) => {
    e.stopPropagation();
    if (!navigator.geolocation) {
      showToast("Geolocalização não suportada no seu navegador.");
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsLocating(false);
        const { latitude, longitude } = pos.coords;
        map.flyTo([latitude, longitude], Math.max(map.getZoom(), 16), {
          duration: 1.0,
        });
        showToast("Localização centralizada.");
      },
      (err) => {
        setIsLocating(false);
        let msg = "Não foi possível obter sua localização.";
        if (err.code === 1) msg = "Permissão de localização negada.";
        else if (err.code === 2) msg = "Sinal de GPS indisponível.";
        else if (err.code === 3) msg = "Tempo limite para localização esgotado.";
        showToast(msg);
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 30000,
      }
    );
  };

  // Alternador de Categoria Temática
  const handleToggleCategory = (catKey) => {
    if (activeCategory === catKey) {
      setThematicCategory(null);
    } else {
      setThematicCategory(catKey);
    }
  };

  const rightOffset = isMobile ? 14 : statsPanelCollapsed ? 14 : 348;
  const bottomOffset = isMobile ? 68 : 14;

  const legendTitle = isChoroplethActive
    ? "Concentração por Bairro"
    : isHeatmapActive
    ? "Densidade Térmica"
    : "Legenda do Mapa";

  return (
    <div
      ref={toolbarRef}
      className="glass-card map-unified-toolbar"
      style={{
        position: "absolute",
        bottom: bottomOffset,
        right: rightOffset,
        transition: "right 0.3s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.2s ease",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 4,
        borderRadius: "var(--radius-md)",
        boxShadow: "var(--shadow-md)",
        background: "var(--bg-glass)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid var(--border-color)",
        userSelect: "none",
      }}
    >
      {/* ── Notificação Toast Flutuante ── */}
      {toastMessage && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            right: 0,
            marginBottom: 8,
            padding: "6px 12px",
            background: "rgba(15, 23, 42, 0.92)",
            color: "#f8fafc",
            fontSize: "var(--text-label)",
            fontWeight: 500,
            borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow-md)",
            whiteSpace: "nowrap",
            zIndex: 1100,
            border: "1px solid rgba(255, 255, 255, 0.12)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            animation: "fadeIn 0.15s ease-out",
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* ── 1. Zoom In (+) ── */}
      <button
        type="button"
        onClick={handleZoomIn}
        aria-label="Aproximar zoom"
        title="Aproximar mapa (+)"
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: "var(--text-primary)",
          cursor: "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "background var(--transition-fast)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>

      {/* Divisor */}
      <div
        style={{
          width: 20,
          height: 1,
          background: "var(--border-subtle)",
          margin: "2px 0",
        }}
      />

      {/* ── 2. Zoom Out (-) ── */}
      <button
        type="button"
        onClick={handleZoomOut}
        aria-label="Afastar zoom"
        title="Afastar mapa (-)"
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: "var(--text-primary)",
          cursor: "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "background var(--transition-fast)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>

      {/* Divisor */}
      <div
        style={{
          width: 20,
          height: 1,
          background: "var(--border-subtle)",
          margin: "2px 0",
        }}
      />

      {/* ── 3. Recentralizar Artur Nogueira ── */}
      <button
        type="button"
        onClick={handleResetView}
        aria-label="Visão geral de Artur Nogueira"
        title="Visão geral de Artur Nogueira"
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "background var(--transition-fast)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="22" y1="12" x2="18" y2="12" />
          <line x1="6" y1="12" x2="2" y2="12" />
          <line x1="12" y1="6" x2="12" y2="2" />
          <line x1="12" y1="22" x2="12" y2="18" />
        </svg>
      </button>

      {/* ── 4. Minha Localização GPS ── */}
      <button
        type="button"
        onClick={handleMyLocation}
        aria-label="Minha localização atual"
        title="Minha localização atual (GPS)"
        disabled={isLocating}
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          border: "none",
          color: isLocating ? "var(--accent-primary)" : "var(--text-secondary)",
          cursor: isLocating ? "wait" : "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "background var(--transition-fast)",
        }}
        onMouseEnter={(e) => {
          if (!isLocating) e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          if (!isLocating) e.currentTarget.style.background = "transparent";
        }}
      >
        {isLocating ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animation: "spin 1s linear infinite" }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="3 11 22 2 13 21 11 13 3 11" />
          </svg>
        )}
      </button>

      {/* Divisor */}
      <div
        style={{
          width: 20,
          height: 1,
          background: "var(--border-subtle)",
          margin: "2px 0",
        }}
      />

      {/* ── 5. Seletor de Camadas (Clusters / Mapa de Calor / Concentração por Bairro) ── */}
      <button
        type="button"
        data-testid="toggle-layers-btn"
        onClick={(e) => {
          e.stopPropagation();
          setIsLayersOpen((prev) => {
            const next = !prev;
            if (next) setIsLegendOpen(false);
            return next;
          });
        }}
        aria-expanded={isLayersOpen}
        aria-haspopup="dialog"
        aria-label={isLayersOpen ? "Fechar seletor de camadas" : "Abrir seletor de camadas"}
        title={isLayersOpen ? "Fechar Camadas" : "Camadas do Mapa"}
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isLayersOpen ? "var(--accent-light)" : "transparent",
          border: isLayersOpen ? "1px solid var(--accent-primary)" : "1px solid transparent",
          color: isLayersOpen ? "var(--accent-primary)" : "var(--text-secondary)",
          cursor: "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "all var(--transition-fast)",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          if (!isLayersOpen) e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          if (!isLayersOpen) e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-layers preview-icon"
        >
          <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
          <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
          <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
        </svg>

        {!isLayersOpen && activeLayerMode !== "clusters" && (
          <span
            style={{
              position: "absolute",
              top: 3,
              right: 3,
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: activeLayerMode === "heatmap" ? "#f97316" : "var(--accent-primary)",
              boxShadow:
                activeLayerMode === "heatmap"
                  ? "0 0 4px #f97316"
                  : "0 0 4px var(--accent-primary)",
            }}
          />
        )}
      </button>

      {/* Divisor */}
      <div
        style={{
          width: 20,
          height: 1,
          background: "var(--border-subtle)",
          margin: "2px 0",
        }}
      />

      {/* ── 6. Legenda do Mapa ── */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsLegendOpen((prev) => {
            const next = !prev;
            if (next) setIsLayersOpen(false);
            return next;
          });
        }}
        aria-label={isLegendOpen ? "Fechar legenda do mapa" : "Abrir legenda do mapa"}
        title={isLegendOpen ? "Fechar Legenda" : "Mostrar Legenda do Mapa"}
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isLegendOpen ? "var(--accent-light)" : "transparent",
          border: isLegendOpen ? "1px solid var(--accent-primary)" : "1px solid transparent",
          color: isLegendOpen ? "var(--accent-primary)" : "var(--text-secondary)",
          cursor: "pointer",
          borderRadius: "var(--radius-sm)",
          transition: "all var(--transition-fast)",
        }}
        onMouseEnter={(e) => {
          if (!isLegendOpen) e.currentTarget.style.background = "var(--border-subtle)";
        }}
        onMouseLeave={(e) => {
          if (!isLegendOpen) e.currentTarget.style.background = "transparent";
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
          <line x1="9" x2="9" y1="3" y2="18" />
          <line x1="15" x2="15" y1="6" y2="21" />
        </svg>
      </button>

      {/* ── 7. Popover Flutuante / Folha Inferior do Seletor de Camadas ── */}
      {isLayersOpen && (
        <div
          ref={layersPopoverRef}
          role="dialog"
          aria-label="Seletor de camadas do mapa"
          className="glass-card map-layers-popover"
          style={
            isMobile
              ? {
                  position: "fixed",
                  left: 12,
                  right: 12,
                  bottom: 68,
                  padding: "12px 14px 14px",
                  borderRadius: "var(--radius-lg)",
                  border: "1px solid var(--border-color)",
                  boxShadow: "var(--shadow-lg), 0 -8px 28px rgba(0, 0, 0, 0.25)",
                  background: "var(--bg-glass)",
                  color: "var(--text-primary)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  zIndex: 1060,
                  animation: "fadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                }
              : {
                  position: "absolute",
                  right: 44,
                  bottom: 0,
                  width: 262,
                  padding: "12px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-color)",
                  boxShadow: "var(--shadow-lg), 0 12px 32px rgba(0, 0, 0, 0.2)",
                  background: "var(--bg-glass)",
                  color: "var(--text-primary)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  zIndex: 1055,
                  animation: "fadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                }
          }
        >
          {/* Alça tátil de folha inferior (apenas mobile) */}
          {isMobile && (
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: "var(--border-color)",
                alignSelf: "center",
                marginBottom: 2,
              }}
            />
          )}

          {/* Cabeçalho do Seletor de Camadas */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: 6,
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-layers preview-icon"
                style={{ color: "var(--accent-primary)" }}
              >
                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
              </svg>
              <h3
                style={{
                  margin: 0,
                  fontSize: "var(--text-body-sm)",
                  fontWeight: 700,
                  letterSpacing: "var(--tracking-tight)",
                }}
              >
                Camadas do Mapa
              </h3>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsLayersOpen(false);
              }}
              aria-label="Fechar seletor de camadas"
              title="Fechar"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-muted)",
                cursor: "pointer",
                padding: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "var(--radius-sm)",
                transition: "color var(--transition-fast)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Lista de Opções de Visualização (Radio-style: clicar no modo já ativo mantém ativo e fecha) */}
          <div
            role="radiogroup"
            aria-label="Modo de visualização do mapa"
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
          >
            {[
              {
                id: "clusters",
                testId: "layer-option-clusters",
                label: "Marcadores (Clusters)",
                description: "Pontos e agrupamentos por tema",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-map-pinned preview-icon"
                  >
                    <path d="M18 8c0 3.613-3.869 7.429-5.393 8.795a1 1 0 01-1.214 0C9.87 15.429 6 11.613 6 8a6 6 0 0112 0" />
                    <path d="M4.474 15h-.197a1 1 0 00-.969.753l-1.097 4.35a1.5 1.5 0 001.444 1.898L20.344 22a1.5 1.5 0 001.446-1.897l-1.098-4.35a1 1 0 00-.969-.753h-.197" />
                    <circle cx="12" cy="8" r="2" />
                  </svg>
                ),
              },
              {
                id: "heatmap",
                testId: "layer-option-heatmap",
                label: "Mapa de Calor",
                description: "Densidade contínua nas vias",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 32 32"
                  >
                    <circle cx="21" cy="20" r="2" fill="currentColor" />
                    <circle cx="14" cy="12" r="2" fill="currentColor" />
                    <circle cx="29" cy="19" r="1" fill="currentColor" />
                    <path
                      fill="currentColor"
                      d="M26.5 30a3.5 3.5 0 1 1 3.5-3.5a3.504 3.504 0 0 1-3.5 3.5m0-5a1.5 1.5 0 1 0 1.5 1.5a1.5 1.5 0 0 0-1.5-1.5M14 30a3.96 3.96 0 0 1-2.126-.621a6.998 6.998 0 1 1 4.11-6.838A3.992 3.992 0 0 1 14 30m-1.884-3.028l.539.495a1.992 1.992 0 1 0 2.004-3.343l-.691-.243l.03-.847a5.009 5.009 0 1 0-2.517 4.302zM24 16a6.007 6.007 0 0 1-6-6a5 5 0 0 1 .027-.533A3.96 3.96 0 0 1 16 6a4.005 4.005 0 0 1 4-4a3.96 3.96 0 0 1 3.467 2.027Q23.738 4 24 4a6 6 0 0 1 0 12M20 4a2 2 0 0 0-2 2a1.98 1.98 0 0 0 1.43 1.902l.902.27l-.215.917A3.994 3.994 0 1 0 24 6a4 4 0 0 0-.912.116l-.916.214l-.27-.9A1.98 1.98 0 0 0 20 4M6.5 11A4.5 4.5 0 1 1 11 6.5A4.505 4.505 0 0 1 6.5 11m0-7A2.5 2.5 0 1 0 9 6.5A2.503 2.503 0 0 0 6.5 4"
                    />
                  </svg>
                ),
              },
              {
                id: "choropleth",
                testId: "layer-option-choropleth",
                label: "Concentração por Bairro",
                description: "Escala analítica em 5 faixas",
                icon: (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 32 32"
                  >
                    <path
                      fill="currentColor"
                      d="m29.243 4.03l-8-2a1 1 0 0 0-.615.042l-9.7 3.88L3.243 4.03A1 1 0 0 0 2 5v22a1 1 0 0 0 .757.97l8 2A1 1 0 0 0 11 30a1 1 0 0 0 .372-.072l9.7-3.88l7.686 1.922A1 1 0 0 0 30 27V5a1 1 0 0 0-.757-.97M28 11h-6V4.28l6 1.5Zm-18 8H4v-6h6Zm2-8V7.677l8-3.2V11Zm8 2v6h-8v-6Zm-8 8h8v3.323l-8 3.2Zm10-8h6v6h-6ZM10 7.78V11H4V6.28ZM4 21h6v6.72l-6-1.5Zm18 3.219V21h6v4.72Z"
                    />
                  </svg>
                ),
              },
            ].map((option) => {
              const isSelected = activeLayerMode === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  data-testid={option.testId}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectLayerMode(option.id);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "var(--radius-sm)",
                    border: isSelected
                      ? "1px solid var(--accent-primary)"
                      : "1px solid var(--border-subtle)",
                    background: isSelected ? "var(--accent-light)" : "transparent",
                    color: isSelected ? "var(--accent-primary)" : "var(--text-primary)",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all var(--transition-fast)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "var(--border-subtle)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "var(--radius-sm)",
                      background: isSelected
                        ? "var(--accent-primary)"
                        : "var(--bg-surface-subtle, rgba(148, 163, 184, 0.14))",
                      color: isSelected ? "#ffffff" : "var(--text-secondary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all var(--transition-fast)",
                    }}
                  >
                    {option.icon}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: "var(--text-label)",
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? "var(--accent-primary)" : "var(--text-primary)",
                        lineHeight: "var(--leading-snug)",
                      }}
                    >
                      {option.label}
                    </span>
                    <span
                      style={{
                        fontSize: "var(--text-micro)",
                        color: "var(--text-muted)",
                        lineHeight: "var(--leading-snug)",
                      }}
                    >
                      {option.description}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 8. Popover Flutuante da Legenda (Ancorado à esquerda no Desktop / Acima no Mobile) ── */}
      {isLegendOpen && (
        <div
          ref={popoverRef}
          className="glass-card map-legend-popover"
          style={{
            position: "absolute",
            right: isMobile ? 0 : 44,
            bottom: isMobile ? 44 : 0,
            width: 262,
            maxHeight: isMobile ? "60vh" : "calc(100vh - 120px)",
            padding: "12px 14px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-lg), 0 12px 32px rgba(0,0,0,0.2)",
            background: "var(--bg-glass)",
            color: "var(--text-primary)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            overflowY: "auto",
            zIndex: 1050,
            animation: "fadeIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
          }}
        >
          {/* Cabeçalho do Card */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingBottom: 6,
              borderBottom: "1px solid var(--border-subtle)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "var(--accent-primary)" }}
              >
                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                <line x1="9" x2="9" y1="3" y2="18" />
                <line x1="15" x2="15" y1="6" y2="21" />
              </svg>
              <h3
                style={{
                  margin: 0,
                  fontSize: "var(--text-body-sm)",
                  fontWeight: 700,
                  letterSpacing: "var(--tracking-tight)",
                }}
              >
                {legendTitle}
              </h3>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsLegendOpen(false);
              }}
              aria-label="Fechar legenda"
              title="Fechar"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-muted)",
                cursor: "pointer",
                padding: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "var(--radius-sm)",
                transition: "color var(--transition-fast)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* ── CENÁRIO A: MAPA COROPLÉTICO POR BAIRRO ATIVO ── */}
          {isChoroplethActive ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                  }}
                >
                  Escala em 5 Faixas (Indicações)
                </span>
                {choroplethMeta?.totalBairrosComDemanda !== undefined && (
                  <span
                    style={{
                      fontSize: "var(--text-micro)",
                      fontWeight: 600,
                      color: "var(--text-secondary)",
                    }}
                  >
                    {choroplethMeta.totalBairrosComDemanda}/132 ativos
                  </span>
                )}
              </div>

              {/* Barra Discreta de 5 Blocos Sequenciais */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 3,
                  width: "100%",
                }}
              >
                {choroplethPalette.slice(1).map((swatch) => (
                  <div
                    key={swatch.classIndex}
                    style={{
                      height: 8,
                      borderRadius: 3,
                      background: swatch.fillColor,
                      border: `1px solid ${swatch.borderColor}`,
                    }}
                  />
                ))}
              </div>

              {/* Lista Detalhada das Faixas (0 a 5) */}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Faixa 0: Sem indicações */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "var(--text-label)",
                    padding: "2px 4px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: choroplethPalette[0].fillColor,
                        border: `1px solid ${choroplethPalette[0].borderColor}`,
                        display: "inline-block",
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ color: "var(--text-secondary)" }}>Sem indicações</span>
                  </div>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      fontSize: "var(--text-micro)",
                    }}
                  >
                    0
                    {choroplethMeta?.classCounts?.[0] !== undefined
                      ? ` (${choroplethMeta.classCounts[0]})`
                      : ""}
                  </span>
                </div>

                {/* Faixas 1 a 5 */}
                {choroplethBreaks.map((b) => {
                  const swatch = choroplethPalette[b.classIndex] || choroplethPalette[1];
                  const bairrosInClass = choroplethMeta?.classCounts?.[b.classIndex];
                  return (
                    <div
                      key={b.classIndex}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "var(--text-label)",
                        padding: "2px 4px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 3,
                            background: swatch.fillColor,
                            border: `1px solid ${swatch.borderColor}`,
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>
                          {swatch.name}
                        </span>
                      </div>
                      <span
                        style={{
                          fontWeight: 700,
                          color: "var(--text-secondary)",
                          fontSize: "var(--text-micro)",
                        }}
                      >
                        {b.label}
                        {bairrosInClass !== undefined ? ` (${bairrosInClass})` : ""}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  height: 1,
                  background: "var(--border-subtle)",
                  margin: "2px 0",
                }}
              />

              {/* Indicador do Contorno Azul/Ciano de Seleção */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "2px 4px",
                  fontSize: "var(--text-label)",
                }}
              >
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: "transparent",
                    border: `2.5px solid ${selectedStrokeColor}`,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
                  Bairro selecionado (contorno)
                </span>
              </div>

              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "var(--text-micro)",
                  color: "var(--text-muted)",
                  lineHeight: "var(--leading-snug)",
                }}
              >
                Clique em um bairro para filtrar os painéis mantendo os 132 bairros comparáveis no
                mapa.
              </p>
            </div>
          ) : isHeatmapActive ? (
            /* ── CENÁRIO B: MAPA DE CALOR ATIVO ── */
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{
                  fontSize: "var(--text-micro)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "var(--tracking-wide)",
                  color: "var(--text-muted)",
                }}
              >
                Concentração de Demandas
              </span>

              {/* Barra de Gradiente Térmico */}
              <div
                style={{
                  width: "100%",
                  height: 12,
                  borderRadius: 6,
                  background:
                    "linear-gradient(to right, #2563eb, #06b6d4, #10b981, #eab308, #f97316, #ef4444)",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "var(--text-micro)",
                  color: "var(--text-secondary)",
                  fontWeight: 600,
                }}
              >
                <span>Baixa</span>
                <span>Média</span>
                <span>Alta</span>
              </div>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "var(--text-label)",
                  color: "var(--text-muted)",
                  lineHeight: "var(--leading-snug)",
                }}
              >
                Zonas quentes destacam vias urbanas e regiões com maior acúmulo contínuo de
                indicações parlamentares.
              </p>
            </div>
          ) : (
            /* ── CENÁRIO C: MODO MARCADORES (TEMAS, GEOMETRIA E PRECISÃO) ── */
            <>
              {/* 1. Categorias Temáticas Oficiais com filtro em 1 clique */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "var(--text-micro)",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "var(--tracking-wide)",
                      color: "var(--text-muted)",
                    }}
                  >
                    Temas Municipais ({THEMATIC_CATEGORIES_ORDER.length})
                  </span>
                  {activeCategory && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setThematicCategory(null);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--accent-primary)",
                        fontSize: "var(--text-micro)",
                        fontWeight: 600,
                        cursor: "pointer",
                        padding: 0,
                      }}
                      title="Remover filtro de tema"
                    >
                      Limpar
                    </button>
                  )}
                </div>

                {THEMATIC_CATEGORIES_ORDER.map((catKey) => {
                  const cat = THEMATIC_CATEGORIES[catKey];
                  if (!cat) return null;
                  const isCatSelected = activeCategory === catKey;

                  return (
                    <div
                      key={cat.key}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCategory(cat.key);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "3px 6px",
                        borderRadius: "var(--radius-sm)",
                        cursor: "pointer",
                        background: isCatSelected ? "var(--accent-light)" : "transparent",
                        border: isCatSelected
                          ? "1px solid var(--accent-primary)"
                          : "1px solid transparent",
                        transition: "all var(--transition-fast)",
                      }}
                      title={`Filtrar por ${cat.label}`}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          background: cat.solid || cat.bgColor,
                          color: cat.onSolid || cat.iconColor,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        }}
                      >
                        <span
                          className="geom-svg-icon"
                          style={{
                            width: 10,
                            height: 10,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "inherit",
                          }}
                          dangerouslySetInnerHTML={{ __html: cat.icon }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: "var(--text-label)",
                          fontWeight: isCatSelected ? 700 : 500,
                          color: isCatSelected
                            ? "var(--accent-primary)"
                            : "var(--text-primary)",
                        }}
                      >
                        {cat.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  height: 1,
                  background: "var(--border-subtle)",
                  margin: "4px 0",
                }}
              />

              {/* 2. Geometrias Espaciais Vetoriais */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                  }}
                >
                  Geometria no Mapa
                </span>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <GeometryIcon
                    icon={GEOMETRY_TYPE_METADATA.LineString.icon}
                    size={14}
                    color="var(--accent-primary)"
                  />
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Trecho de via (Linha)
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <GeometryIcon
                    icon={GEOMETRY_TYPE_METADATA.Polygon.icon}
                    size={14}
                    color="var(--accent-primary)"
                  />
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Bairro / Região (Polígono)
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <GeometryIcon
                    icon={GEOMETRY_TYPE_METADATA.MultiPoint.icon}
                    size={14}
                    color="var(--accent-primary)"
                  />
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Cruzamento de vias
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <GeometryIcon
                    icon={GEOMETRY_TYPE_METADATA.Point.icon}
                    size={14}
                    color="var(--accent-primary)"
                  />
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Endereço pontual
                  </span>
                </div>
              </div>

              <div
                style={{
                  height: 1,
                  background: "var(--border-subtle)",
                  margin: "4px 0",
                }}
              />

              {/* 3. Precisão do Endereço */}
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <span
                  style={{
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "var(--tracking-wide)",
                    color: "var(--text-muted)",
                  }}
                >
                  Precisão do Ponto
                </span>

                {/* Exato */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#16a34a",
                      border: "1.5px solid #ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="6" height="6" viewBox="0 0 10 10">
                      <circle cx="5" cy="5" r="3.5" fill="#ffffff" />
                    </svg>
                  </div>
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Endereço exato
                  </span>
                </div>

                {/* Aproximado */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "2px 6px",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: "#475569",
                      border: "1.5px solid #ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: -2,
                        right: -2,
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#f59e0b",
                        border: "1px solid #ffffff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                      }}
                    />
                  </div>
                  <span style={{ fontSize: "var(--text-label)", color: "var(--text-secondary)" }}>
                    Endereço aproximado
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
